import React, { useEffect, useMemo, useRef, useState } from "react";

// Type-to-search picker used in place of "click → table pop-up → filter pop-up".
// options: [{ value, label, meta, disabled, disabledReason }]
// Typing filters on label, value and meta; ↑/↓ + Enter or click picks; Esc closes.
const SearchSelect = ({
  id,
  options = [],
  value,
  onSelect,
  placeholder = "Type to search",
  loading = false,
  disabled = false,
  error,
  icon = "fa fa-building",
  emptyText = "No matches",
  maxShown = 50,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? options.filter((o) =>
          [o.label, o.value, o.meta].some((t) => String(t || "").toLowerCase().includes(q)),
        )
      : options;
    return list.slice(0, maxShown);
  }, [options, query, maxShown]);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => setActive(0), [query, open]);

  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const pick = (o) => {
    if (!o || o.disabled) return;
    onSelect(o);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (open && matches[active]) {
        e.preventDefault();
        pick(matches[active]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Show the chosen item while closed; the search text while typing
  const shown = open ? query : selected ? `${selected.label} (${selected.value})` : "";

  return (
    <div ref={wrapRef} className="relative">
      <i className={`${icon} absolute left-3 top-3 text-[#FF702B] text-sm`}></i>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        value={shown}
        placeholder={selected ? `${selected.label} (${selected.value})` : placeholder}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        className={`w-full h-[42px] rounded-lg border pl-9 pr-8 text-sm placeholder-gray-400
          focus:outline-none focus:ring-0 focus:border-[#FF702B]
          ${error ? "border-red-500" : "border-gray-300"}
          ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}`}
      />
      <span className="absolute right-3 top-3 text-gray-400 text-xs pointer-events-none">
        {loading ? "…" : "▾"}
      </span>

      {open && !disabled && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto"
          style={{ maxHeight: "240px", zIndex: 20 }}
        >
          {loading ? (
            <li className="px-3 py-2 text-sm text-gray-500">Loading…</li>
          ) : matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">{emptyText}</li>
          ) : (
            matches.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                aria-disabled={o.disabled || undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}
                onMouseEnter={() => setActive(i)}
                className={`px-3 py-2 text-sm ${
                  o.disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer"
                } ${i === active ? "bg-orange-50" : ""}`}
                title={o.disabled ? o.disabledReason : undefined}
              >
                <div className="flex justify-between gap-2">
                  <span className="font-medium truncate">{o.label}</span>
                  <span className="text-gray-500 text-xs whitespace-nowrap">{o.value}</span>
                </div>
                {(o.meta || o.disabled) && (
                  <div className="text-xs text-gray-500 truncate">
                    {o.disabled ? o.disabledReason : o.meta}
                  </div>
                )}
              </li>
            ))
          )}
          {!loading && options.length > maxShown && matches.length === maxShown && (
            <li className="px-3 py-1 text-xs text-gray-400">Showing first {maxShown} — type to narrow down</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchSelect;
